import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetALL } from "../../../../../.globals/redux/actions";
import { endpoints } from "../../../../../core/defaultValues";
import { clientQuery } from "../../../../../core/data/GraphQLApi";
import { ExceptionManager } from "../../../../../core/logManager";
import moment from "moment";

const INIT_STATE = {
    loading: false,
    driver: null,
    error: null,
    loadingDriversSummary: false,
    driversSummary: null,
    driversSummaryError: null,
    loadingZoneStats: false,
    zoneStats: null,
    zoneStatsError: null
};
const route = 'modules/driverPerformance/views/driverDetail/redux/slice';

export const getDriverPerformanceDriver = createAsyncThunk('driverPerformance/views/driverDetail/getDriverPerformanceDriver', async (payload, { rejectWithValue, getState }) => {
    try {
        if (!payload?.driverId) {
            return null;
        }

        const response = await clientQuery(`
            query ($startDate: AWSDateTime!, $endDate: AWSDateTime!, $driverId: Int, $eventNames: [String]) {
                data: getDriverPerformanceDriver(
                    startDate: $startDate
                    endDate: $endDate
                    driverId: $driverId
                    eventNames: $eventNames
                ) {
                    driverId
                    driverName
                    driverTags
                    driverTagsNames
                    totalEvents
                    totalDistance
                    avgEvents
                    riskEvents
                    riskScore
                    supervisorId
                    supervisorName
                }
            }
        `,
            {
                driverId: payload?.driverId || [],
                startDate: payload?.startDate || moment().subtract(1, 'month').startOf('day').toISOString(),
                endDate: payload?.endDate || moment().endOf('day').toISOString(),
                eventNames: payload?.events?.length > 0 ? payload?.events : []
            },
            endpoints.GRAPHQL_GENERAL_V2
        );

        return response?.data || null;

    } catch (exc) {
        ExceptionManager(exc, route, 'getDriverPerformanceDriver');
        return rejectWithValue(exc);
    }
});

export const getDriversSummary = createAsyncThunk('driverPerformance/views/driverDetail/getDriversSummary', async (payload, { rejectWithValue, getState }) => {
    try {
        if (!payload?.driverId || !payload?.startDate || !payload?.endDate) {
            return null;
        }

        const response = await clientQuery(`
            query ($startDate: AWSDateTime!, $endDate: AWSDateTime!, $driverId: Int) {
                data: getDriversSummary(
                    startDate: $startDate
                    endDate: $endDate
                    driverId: $driverId
                ) {
                    drivers {
                        driverId
                        driverName
                        avgSeverity
                        normalizedAvgEvents
                        riskScore
                        totalDistance_km
                    }
                }
            }
        `,
            {
                driverId: payload?.driverId,
                startDate: payload?.startDate,
                endDate: payload?.endDate
            },
            endpoints.GRAPHQL_GENERAL_V2
        );

        return response?.data || null;

    } catch (exc) {
        ExceptionManager(exc, route, 'getDriversSummary');
        return rejectWithValue(exc);
    }
});

export const getHeatmapZoneStats = createAsyncThunk('driverPerformance/views/driverDetail/getHeatmapZoneStats', async (payload, { rejectWithValue }) => {
    try {
        const { startDate, endDate, minSeverity, maxSeverity, minEvents, maxEvents, driverIds, tagIds } = payload;

        const response = await clientQuery(`
            query GetHeatmapZoneStats(
                $startDate: AWSDateTime!
                $endDate: AWSDateTime!
                $minSeverity: Float!
                $maxSeverity: Float!
                $minEvents: Float!
                $maxEvents: Float!
                $driverIds: [Int]
                $tagIds: [Int]
            ) {
                data: getHeatmapZoneStats(
                    startDate: $startDate
                    endDate: $endDate
                    minSeverity: $minSeverity
                    maxSeverity: $maxSeverity
                    minEvents: $minEvents
                    maxEvents: $maxEvents
                    driverIds: $driverIds
                    tagIds: $tagIds
                ) {
                    driverCount
                    avgRiskScore
                    avgSeverity
                    avgEventCount
                    avgDistance
                }
            }
        `,
            {
                startDate,
                endDate,
                minSeverity,
                maxSeverity,
                minEvents,
                maxEvents,
                driverIds: driverIds || null,
                tagIds: tagIds || null
            },
            endpoints.GRAPHQL_GENERAL_V2
        );

        return response?.data || null;

    } catch (exc) {
        ExceptionManager(exc, route, 'getHeatmapZoneStats');
        return rejectWithValue(exc);
    }
});

//Slice
const DriverPerformanceDriverViewRouteRedux = createSlice({
    name: "driverPerformance/driver/driverPerformanceDriverViewRoute",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        builder.addCase(getDriverPerformanceDriver.pending, (state) => {
            state.loadingEvents = true;
            state.error = null;
        });
        builder.addCase(getDriverPerformanceDriver.fulfilled, (state, action) => {
            state.loadingEvents = false;
            state.driver = action.payload;
        });
        builder.addCase(getDriverPerformanceDriver.rejected, (state, action) => {
            state.loadingEvents = false;
            state.error = action.payload;
        });

        builder.addCase(getDriversSummary.pending, (state) => {
            state.loadingDriversSummary = true;
            state.driversSummaryError = null;
        });
        builder.addCase(getDriversSummary.fulfilled, (state, action) => {
            state.loadingDriversSummary = false;
            state.driversSummary = action.payload;
        });
        builder.addCase(getDriversSummary.rejected, (state, action) => {
            state.loadingDriversSummary = false;
            state.driversSummaryError = action.payload;
        });

        builder.addCase(getHeatmapZoneStats.pending, (state) => {
            state.loadingZoneStats = true;
            state.zoneStatsError = null;
        });
        builder.addCase(getHeatmapZoneStats.fulfilled, (state, action) => {
            state.loadingZoneStats = false;
            state.zoneStats = action.payload;
        });
        builder.addCase(getHeatmapZoneStats.rejected, (state, action) => {
            state.loadingZoneStats = false;
            state.zoneStatsError = action.payload;
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = DriverPerformanceDriverViewRouteRedux;

// Extract and export each action creator by name
export const { reset } = actions;

// Export the reducer, either as a default or named export
export default reducer;
